import * as React from 'react';
import { ChevronDown, Layers, List, Search } from 'lucide-react';

import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../ui/table';
import { cn } from '../../lib/utils';
import { Montant } from './VisuelsRapport';
import { montant } from './reportHelpers';

/**
 * Détail d'un transporteur de la synthèse par camion.
 *
 * Les camions pris en location se rangent sous leur seul transporteur. Le
 * découpage par marque n'est qu'une vue de ces camions: il s'ouvre dans une
 * fenêtre, au clic d'un bouton, et n'existe que si le transporteur a plus d'une
 * marque. La liste complète des transporteurs s'ouvre de la même façon quand
 * il y en a trop pour des cartes.
 */

/** Au-delà de ce nombre de transporteurs, la synthèse n'affiche que les meilleurs. */
export const MAX_CARTES_TRANSPORTEURS = 8;
/** Une recherche n'apparaît dans la liste complète que si elle est longue. */
const SEUIL_RECHERCHE = 12;

export const marquesDuGroupe = (groupe) => (Array.isArray(groupe?.brands) ? groupe.brands : []);

/** Le bouton « Par marque » n'existe que pour un transporteur de plusieurs marques. */
export const aPlusieursMarques = (groupe) => marquesDuGroupe(groupe).length > 1;

const Chiffres = ({ ligne }) => (
  <>
    <TableCell className="text-right tabular-nums">{ligne.trucks_count ?? 0}</TableCell>
    <TableCell className="text-right tabular-nums">{ligne.rotations || '—'}</TableCell>
    <TableCell className="text-right">
      <Montant valeur={ligne.revenue} />
    </TableCell>
    <TableCell className="text-right">
      <Montant valeur={ligne.charges} />
    </TableCell>
    <TableCell className="text-right">
      <Montant valeur={ligne.result} signe fort />
    </TableCell>
  </>
);

const EnteteChiffres = () => (
  <>
    <TableHead className="text-right">Camions</TableHead>
    <TableHead className="text-right">Rotations</TableHead>
    <TableHead className="text-right">Recettes</TableHead>
    <TableHead className="text-right">Charges</TableHead>
    <TableHead className="text-right">Résultat</TableHead>
  </>
);

/** Fenêtre « Par marque » d'un transporteur: une ligne par marque, puis le sous-total. */
export const FenetreMarques = ({ groupe, ouvert, onOuvertChange }) => {
  const marques = marquesDuGroupe(groupe);
  const sousTotal = groupe?.subtotal || {};
  return (
    <Dialog open={ouvert} onOpenChange={onOuvertChange}>
      <DialogContent className="max-w-2xl" data-testid="fenetre-marques">
        <DialogHeader>
          <DialogTitle>Par marque · {groupe?.label}</DialogTitle>
          <DialogDescription className="sr-only">
            Camions, rotations, recettes, charges et résultat de {groupe?.label}, marque par marque.
          </DialogDescription>
        </DialogHeader>
        <Table compact>
          <TableHeader>
            <TableRow>
              <TableHead>Marque</TableHead>
              <EnteteChiffres />
            </TableRow>
          </TableHeader>
          <TableBody>
            {marques.map((marque) => (
              <TableRow key={marque.brand}>
                <TableCell className="font-medium">{marque.brand}</TableCell>
                <Chiffres ligne={marque} />
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="font-semibold">Sous-total</TableCell>
              <Chiffres ligne={sousTotal} />
            </TableRow>
          </TableFooter>
        </Table>
      </DialogContent>
    </Dialog>
  );
};

/** Bouton « Par marque » d'une carte de transporteur, avec sa fenêtre. */
export const BoutonMarques = ({ groupe }) => {
  const [ouvert, setOuvert] = React.useState(false);
  if (!aPlusieursMarques(groupe)) return null;
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 shrink-0 gap-1 px-2 text-xs"
        aria-label={`Par marque, ${groupe.label}`}
        onClick={() => setOuvert(true)}
      >
        <Layers aria-hidden="true" />
        Par marque
      </Button>
      <FenetreMarques groupe={groupe} ouvert={ouvert} onOuvertChange={setOuvert} />
    </>
  );
};

/** Une ligne de transporteur, dépliable sur ses marques quand il en a plusieurs. */
const LigneTransporteur = ({ groupe }) => {
  const [deplie, setDeplie] = React.useState(false);
  const marques = aPlusieursMarques(groupe) ? marquesDuGroupe(groupe) : [];
  const sousTotal = groupe.subtotal || {};
  return (
    <>
      <TableRow>
        <TableCell className="max-w-[16rem] font-medium">
          {marques.length > 0 ? (
            <button
              type="button"
              aria-expanded={deplie}
              aria-label={`Par marque, ${groupe.label}`}
              onClick={() => setDeplie((v) => !v)}
              className="flex w-full min-w-0 items-center gap-1.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronDown
                className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', !deplie && '-rotate-90')}
                aria-hidden="true"
              />
              <span className="truncate" title={groupe.label}>
                {groupe.label}
              </span>
            </button>
          ) : (
            <span className="block truncate pl-5" title={groupe.label}>
              {groupe.label}
            </span>
          )}
        </TableCell>
        <Chiffres ligne={sousTotal} />
      </TableRow>
      {deplie &&
        marques.map((marque) => (
          <TableRow key={marque.brand} className="bg-muted/30 text-muted-foreground">
            <TableCell className="pl-9 text-xs">{marque.brand}</TableCell>
            <Chiffres ligne={marque} />
          </TableRow>
        ))}
    </>
  );
};

/** Tous les transporteurs du mois, du meilleur au moins bon résultat. */
export const FenetreTransporteurs = ({ groupes, ouvert, onOuvertChange }) => {
  const [recherche, setRecherche] = React.useState('');
  const classes = React.useMemo(
    () => [...groupes].sort((a, b) => montant(b.subtotal?.result) - montant(a.subtotal?.result)),
    [groupes],
  );
  const terme = recherche.trim().toLowerCase();
  const visibles = terme ? classes.filter((g) => String(g.label).toLowerCase().includes(terme)) : classes;
  return (
    <Dialog open={ouvert} onOpenChange={onOuvertChange}>
      <DialogContent className="max-w-3xl" data-testid="fenetre-transporteurs">
        <DialogHeader>
          <DialogTitle>Transporteurs · {groupes.length}</DialogTitle>
          <DialogDescription className="sr-only">
            Camions, rotations, recettes, charges et résultat de chaque transporteur, du meilleur au moins bon résultat.
          </DialogDescription>
        </DialogHeader>
        {groupes.length > SEUIL_RECHERCHE && (
          <div className="relative w-full sm:w-64">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              value={recherche}
              onChange={(evenement) => setRecherche(evenement.target.value)}
              placeholder="Chercher un transporteur"
              aria-label="Chercher un transporteur"
              className="pl-8"
            />
          </div>
        )}
        <Table compact stickyFirstColumn={false}>
          <TableHeader>
            <TableRow>
              <TableHead>Transporteur</TableHead>
              <EnteteChiffres />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibles.map((groupe) => (
              <LigneTransporteur key={groupe.label} groupe={groupe} />
            ))}
            {visibles.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  Aucun transporteur trouvé
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
};

/** Carte « Voir tous »: ouvre la liste complète des transporteurs. */
export const CarteVoirTous = ({ groupes }) => {
  const [ouvert, setOuvert] = React.useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOuvert(true)}
        className="flex min-h-[5.5rem] items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-card px-4 py-3 text-sm font-semibold text-foreground shadow-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <List className="h-4 w-4" aria-hidden="true" />
        Voir tous les transporteurs ({groupes.length})
      </button>
      <FenetreTransporteurs groupes={groupes} ouvert={ouvert} onOuvertChange={setOuvert} />
    </>
  );
};
